import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { loopClientEntriesSelector } from "../../store/selectors/clientEntries";
import { addNewLoopEntry, deleteLoopEntry } from "../../store/actions/client";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { createNewRow, isPrePopTableSheet } from "../../lib/utils";
import { getFormattedValue } from "../../lib/fields";
import { TableScreenModal } from "./";
import { DeleteButton } from "../client/DeleteButton";
import { referenceDataEndpointSelector, prePopulationLimitSelector } from "../../store/selectors/clientSettings";
import { referenceDataFileListSelector } from "../../store/selectors/referenceData";
import { useReferenceDataInDispatchSelector } from "../../store/selectors/entities/form";
import { usePreFillRowsEffect } from "../../lib/customHooks/usePreFillEffect";

// getScreenRows :: (Array, Object) -> Array
const getScreenRows = (screenValues, {
  guid
}) => [...new Set(screenValues.reduce((rowGuids, {
  keyfieldGuid,
  rowGuid
}) => {
  if (keyfieldGuid === guid) {
    rowGuids.push(rowGuid);
  }
  return rowGuids;
}, []))];
export const EditableTableScreen = ({
  screenName,
  keyField,
  entries,
  sheet
}) => {
  const allEntries = [keyField, ...entries];
  const loopValues = useSelector(loopClientEntriesSelector);
  const refDataEndpoint = useSelector(referenceDataEndpointSelector);
  const refDataFileList = useSelector(referenceDataFileListSelector);
  const useReferenceDataInDispatch = useSelector(useReferenceDataInDispatchSelector);
  const prePopulationLimit = useSelector(prePopulationLimitSelector);
  const [{
    activeRowGuid,
    isNewRow = false
  }, setActiveRowEditingState] = useState(false);
  const [isFetchingRefData, setIsFetchingRefData] = useState(false);
  const dispatch = useDispatch();
  const isPrepopulatedGrid = isPrePopTableSheet(sheet);

  // get unique rowGuids
  const rowGuids = getScreenRows(loopValues, keyField);

  // make sure there are no session saved values to begin with
  const isNewDispatch = !loopValues.length && !rowGuids.length;
  const shouldPrepop = isPrepopulatedGrid && refDataFileList.length && useReferenceDataInDispatch && isNewDispatch;

  /*
   * TODO: Handle large data sets for pre-populated grids in a future sprint
   * For now, REF_DATA_ENTRY_LIMIT sets a limit to the number of rows that can be pre-populated. This is for two reasons:
   * 1: For some reason, if there are more than 180 or so rows, they get cut off in the accordion on the Prefill Form page
   *    in Create Dispatch. That should be an easy fix if we decide to allow more than 150 rows.
   * 2: The load time for large data sets is extremely slow, taking 3-4 minutes to load a pre-populated grid w/ 4 columns
   *    and 10,000 rows of reference data. Fetching the data and transforming it into the right format for the Redux
   *    store only takes 2-3 seconds. The performance hit occurs when adding the client entries (which would be 40,000
   *    entries using the example above) to the Redux store.
   */
  usePreFillRowsEffect({
    shouldRun: shouldPrepop,
    interactiveClient: true,
    entries: allEntries,
    sheet,
    limit: prePopulationLimit,
    setLoadingState: setIsFetchingRefData
  }, []);
  const smartPreviewItemLoopLookup = guid => {
    return rowGuids.map((rowGuid, i) => {
      // Find all values for a given row.
      const entry = loopValues.find(loopItem => {
        if (loopItem.parentGuid == guid && loopItem.rowGuid === rowGuid) {
          return loopItem;
        }
      }) || {
        value: '',
        type: ''
      };
      const columnValue = getFormattedValue(entry);
      const className = `cell cell--editable ${isNewRow && rowGuid === activeRowGuid ? ` cell--new-row` : ``}`;
      return <div className={className} key={`col-${i}`} onClick={() => setActiveRowEditingState({
        activeRowGuid: rowGuid
      })}>
          <div className="loop--value userAdded">{columnValue}</div>
        </div>;
    });
  };
  return isFetchingRefData ? <div className="table-view--loader-container">
      <Loader type="TailSpin" color={'#039de7'} />
    </div> : <div className="tableview--preview editable-table--screen">
      <div className="table-view--wrapper">
        <div className="table">
          {allEntries.map(({
          guid,
          label,
          default: defaultValue
        }, i) => {
          return <div className="col" key={`${label}-key-${i}`}>
                <div className="field--name">{label}</div>
                <div className="cell default--values">
                  <div className="loop--value">{defaultValue}</div>
                </div>
                {smartPreviewItemLoopLookup(guid)}
                <div className="cell">
                  <div className="loop--value">{` `}</div>
                </div>
              </div>;
        })}
          <div className="col control-col">
            <div className="field--name">{` `}</div>
            <div className="cell default--values">
              <div className="loop--value">{` `}</div>
            </div>
            {rowGuids.map(rowGuid => {
            const className = `cell cell--delete ${isNewRow && rowGuid === activeRowGuid ? ` cell--new-row` : ``}`;
            return <div key={rowGuid} className={className}>
                  <DeleteButton onConfirm={() => dispatch(deleteLoopEntry({
                activeRowGuid: rowGuid
              }))} />
                </div>;
          })}
            <div className="cell">
              <div className="loop--value">{` `}</div>
            </div>
          </div>
        </div>
      </div>

      <button type="button" className="foundry--button button--contained button--active btn--sm add-item" onClick={() => {
      const newLoopRow = createNewRow(allEntries);
      const {
        activeRowGuid
      } = newLoopRow;
      dispatch(addNewLoopEntry(newLoopRow));
      setActiveRowEditingState({
        activeRowGuid,
        isNewRow: true
      });
    }} disabled={Boolean(activeRowGuid)}>
        <FontAwesomeIcon className="preview--loop--addItem" icon={faPlus} />
        <span className="button--label">Add Item</span>
      </button>
      {activeRowGuid && <TableScreenModal screenName={screenName} rowGuid={activeRowGuid} isNewRow={isNewRow} keyField={keyField} allowDuplicates={!!sheet.allow_duplicate} deleteRow={() => dispatch(deleteLoopEntry({
      activeRowGuid
    }))} closeModal={() => setActiveRowEditingState(false)} />}
    </div>;
};